import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

// Icon assets from Figma
const healMeLogo = "/images/HealmeLogo.png"

/**
 * CredentialsSection component that displays practitioner credentials
 * Matches the Figma design with proper theme integration
 *
 * @param {Object} section - Section configuration
 * @param {Object} practice - Practice data (optional, for fallback credentials)
 * @param {Object} theme - Theme configuration
 * @param {string} highlightedField - Currently highlighted field
 */

const CredentialsSection = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight || !colors.bgLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()
  const baseBackgroundColor = theme?.colors?.white || "#FFFFFF"
  const sectionBackgroundColor = settings.showBackgroundColor
    ? settings.backgroundColor && settings.backgroundColor !== "theme"
      ? settings.backgroundColor
      : themeColors.bgLight || theme?.colors?.bgLight || baseBackgroundColor
    : baseBackgroundColor
  const credentialCardBackground = settings.showBackgroundColor
    ? "#FFFFFF"
    : theme?.colors?.secondary || themeColors.bgExtraLight || "#F9F0E7"
  const credentialCardBorder = settings.showBackgroundColor ? themeColors.bgMedium || "#E5DED9" : "transparent"

  // Get credentials from settings or fallback to practice credentials
  const getCredentials = () => {
    if (settings.credentials && settings.credentials.length > 0) {
      return settings.credentials
    }

    // Fallback to practice credentials if available
    if (practice?.proCredentials && practice.proCredentials.length > 0) {
      return practice.proCredentials.map((cred, index) => ({
        id: cred.id || index,
        title: cred.credential?.name || cred.name || "Credential",
        organization: cred.issuer?.name || cred.issuer_name || ""
      }))
    }

    return []
  }

  const credentials = getCredentials()

  const renderCredential = (credential) => (
    <div
      key={credential.id}
      className={twMerge(
        "relative box-border flex w-full shrink-0 content-stretch items-center justify-start gap-6 px-6 py-5",
        getThemeShapeClasses(theme)
      )}
      style={{
        backgroundColor: credentialCardBackground,
        border: `1px solid ${credentialCardBorder}`,
        boxShadow: settings.showBackgroundColor ? "0px 12px 24px rgba(15, 23, 42, 0.08)" : "none"
      }}>
      <div className="relative flex w-full shrink-0 content-stretch items-center justify-start gap-4">
        {/* License Icon */}
        <div className="relative size-8 shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 26 26"
            fill="none"
            className="h-full w-full">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8.47067 2.06534C9.03328 1.41631 9.72897 0.895942 10.5105 0.539575C11.292 0.183209 12.1411 -0.000813749 13 2.70491e-06C14.8093 2.70491e-06 16.4307 0.800003 17.5293 2.06534C18.3863 2.00415 19.2463 2.12811 20.0511 2.42881C20.8559 2.72951 21.5865 3.19989 22.1933 3.808C22.8012 4.41475 23.2714 5.14517 23.5721 5.94968C23.8728 6.75419 23.9969 7.61397 23.936 8.47067C24.5848 9.03341 25.1049 9.72915 25.461 10.5107C25.8172 11.2922 26.001 12.1412 26 13C26.0008 13.8589 25.8168 14.708 25.4604 15.4895C25.1041 16.271 24.5837 16.9667 23.9347 17.5293C23.9956 18.386 23.8715 19.2458 23.5708 20.0503C23.2701 20.8548 22.7999 21.5852 22.192 22.192C21.5853 22.7999 20.8548 23.2701 20.0503 23.5708C19.2458 23.8715 18.386 23.9956 17.5293 23.9347C16.9667 24.5837 16.271 25.1041 15.4895 25.4604C14.708 25.8168 13.8589 26.0008 13 26C12.1411 26.0008 11.292 25.8168 10.5105 25.4604C9.72897 25.1041 9.03328 24.5837 8.47067 23.9347C7.61384 23.996 6.75384 23.8723 5.94908 23.5718C5.14431 23.2713 4.41364 22.8012 3.80667 22.1933C3.19863 21.5864 2.72829 20.8558 2.4276 20.051C2.12691 19.2463 2.00291 18.3863 2.064 17.5293C1.41522 16.9666 0.895103 16.2709 0.53897 15.4893C0.182837 14.7078 -0.00097815 13.8588 3.91446e-06 13C3.91446e-06 11.1907 0.800004 9.56933 2.06534 8.47067C2.00435 7.61396 2.12841 6.75415 2.4291 5.94963C2.72979 5.14511 3.20006 4.4147 3.808 3.808C4.4147 3.20006 5.14511 2.72978 5.94963 2.4291C6.75416 2.12841 7.61396 2.00434 8.47067 2.06534ZM17.8133 10.5813C17.8933 10.4747 17.9512 10.3532 17.9836 10.2239C18.016 10.0946 18.0221 9.96019 18.0018 9.82847C17.9815 9.69676 17.935 9.57043 17.8651 9.45693C17.7953 9.34343 17.7034 9.24504 17.595 9.16754C17.4866 9.09004 17.3638 9.03499 17.2337 9.00564C17.1037 8.97629 16.9692 8.97322 16.838 8.99662C16.7068 9.02002 16.5816 9.0694 16.4697 9.14188C16.3579 9.21436 16.2616 9.30847 16.1867 9.41867L11.872 15.4587L9.70667 13.2933C9.5171 13.1167 9.26638 13.0205 9.00731 13.0251C8.74824 13.0297 8.50106 13.1346 8.31784 13.3178C8.13462 13.5011 8.02967 13.7482 8.0251 14.0073C8.02053 14.2664 8.1167 14.5171 8.29334 14.7067L11.2933 17.7067C11.396 17.8092 11.5197 17.8882 11.656 17.9382C11.7922 17.9881 11.9377 18.0078 12.0824 17.9959C12.227 17.984 12.3673 17.9407 12.4935 17.8691C12.6197 17.7975 12.7289 17.6993 12.8133 17.5813L17.8133 10.5813Z"
              fill={theme?.colors?.primary}
            />
          </svg>
        </div>

        {/* Text Content */}
        <div className="relative flex min-h-px min-w-px shrink-0 grow basis-0 flex-col content-stretch items-start justify-start leading-[0]">
          <div
            className="relative min-w-full shrink-0 text-[18px] not-italic"
            style={{
              fontFamily: theme?.fonts?.paragraph,
              fontWeight: theme?.fonts?.paragraphBoldWeight,
              lineHeight: "32px",
              color: theme?.colors?.textDark
            }}>
            <p className="leading-[32px]">{credential.title}</p>
          </div>

          {credential.organization && (
            <div
              className="relative w-full shrink-0 text-[14px] font-normal"
              style={{
                fontFamily: theme?.fonts?.paragraph,
                fontWeight: theme?.fonts?.paragraphRegularWeight,
                lineHeight: "20px",
                color: theme?.colors?.textDark
              }}>
              <p className="leading-[20px]">{credential.organization}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderVerifiedBadge = () => (
    <div className="flex items-center justify-center gap-3">
      {/* Heal.me Logo */}
      <div className="relative flex h-8 w-8 items-center justify-center">
        <img src={healMeLogo} alt="Heal.me" className="h-8 w-8 rounded-[4px] object-contain" />

        {/* Checkmark overlay */}
        <div className="absolute -bottom-1 -right-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <g clipPath="url(#clip0_7597_2049)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.73534 2.53267C6.01664 2.20815 6.36448 1.94797 6.75525 1.76979C7.14601 1.5916 7.57053 1.49959 8 1.5C8.90467 1.5 9.71534 1.9 10.2647 2.53267C10.6931 2.50207 11.1232 2.56406 11.5255 2.7144C11.9279 2.86475 12.2932 3.09994 12.5967 3.404C12.9006 3.70738 13.1357 4.07258 13.2861 4.47484C13.4364 4.87709 13.4985 5.30698 13.468 5.73533C13.7924 6.0167 14.0525 6.36457 14.2305 6.75533C14.4086 7.14609 14.5005 7.57058 14.5 8C14.5004 8.42947 14.4084 8.854 14.2302 9.24476C14.052 9.63552 13.7919 9.98336 13.4673 10.2647C13.4978 10.693 13.4357 11.1229 13.2854 11.5252C13.1351 11.9274 12.8999 12.2926 12.596 12.596C12.2926 12.8999 11.9274 13.135 11.5252 13.2854C11.1229 13.4357 10.693 13.4978 10.2647 13.4673C9.98336 13.7918 9.63552 14.052 9.24476 14.2302C8.854 14.4084 8.42947 14.5004 8 14.5C7.57053 14.5004 7.14601 14.4084 6.75525 14.2302C6.36448 14.052 6.01664 13.7918 5.73534 13.4673C5.30692 13.498 4.87692 13.4361 4.47454 13.2859C4.07216 13.1357 3.70682 12.9006 3.40334 12.5967C3.09931 12.2932 2.86415 11.9279 2.7138 11.5255C2.56346 11.1231 2.50145 10.6931 2.532 10.2647C2.20761 9.9833 1.94755 9.63543 1.76949 9.24467C1.59142 8.85391 1.49951 8.42942 1.5 8C1.5 7.09533 1.9 6.28467 2.53267 5.73533C2.50217 5.30698 2.5642 4.87708 2.71455 4.47482C2.86489 4.07255 3.10003 3.70735 3.404 3.404C3.70735 3.10003 4.07255 2.86489 4.47482 2.71455C4.87708 2.5642 5.30698 2.50217 5.73534 2.53267ZM10.4067 6.79067C10.4467 6.73737 10.4756 6.67661 10.4918 6.61197C10.508 6.54732 10.5111 6.4801 10.5009 6.41424C10.4907 6.34838 10.4675 6.28522 10.4326 6.22847C10.3976 6.17171 10.3517 6.12252 10.2975 6.08377C10.2433 6.04502 10.1819 6.0175 10.1169 6.00282C10.0519 5.98814 9.98459 5.98661 9.91899 5.99831C9.85338 6.01001 9.79078 6.0347 9.73485 6.07094C9.67893 6.10718 9.63081 6.15423 9.59334 6.20933L7.436 9.22933L6.35334 8.14667C6.25855 8.05835 6.13319 8.01027 6.00365 8.01255C5.87412 8.01484 5.75053 8.06731 5.65892 8.15892C5.56731 8.25053 5.51484 8.37412 5.51255 8.50365C5.51027 8.63319 5.55835 8.75855 5.64667 8.85333L7.14667 10.3533C7.198 10.4046 7.25987 10.4441 7.32799 10.4691C7.39612 10.4941 7.46886 10.5039 7.54118 10.498C7.61349 10.492 7.68364 10.4704 7.74675 10.4346C7.80987 10.3988 7.86444 10.3497 7.90667 10.2907L10.4067 6.79067Z"
                fill="#0D9DA4"
              />
            </g>
            <defs>
              <clipPath id="clip0_7597_2049">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
      </div>

      {/* Text */}
      <div
        className="text-[12px] font-semibold uppercase tracking-[0.75px]"
        style={{
          fontFamily: theme?.fonts?.paragraph,
          fontWeight: theme?.fonts?.paragraphBoldWeight,
          color: theme?.colors?.textDark
        }}>
        <a href={window.location.origin} target="_blank" rel="noopener noreferrer" className="hover:underline">
          VERIFIED BY HEAL.ME
        </a>
      </div>
    </div>
  )

  const renderContent = () => (
    <div className="relative flex w-full shrink-0 flex-col content-stretch items-center justify-start gap-10 px-4">
      {/* Title - Always show "Credentials" */}
      <div
        className="relative shrink-0 text-nowrap text-center font-['Playfair_Display:Bold',_sans-serif] text-[40px] font-bold leading-[0]"
        style={{
          fontFamily: theme?.fonts?.heading,
          fontWeight: theme?.fonts?.headingWeight,
          lineHeight: "48px",
          color: theme?.colors?.textDark
        }}>
        <p className="whitespace-pre leading-[48px]">Credentials</p>
      </div>

      {/* Credentials List */}
      {credentials.length > 0 ? (
        <div
          className="relative flex w-full shrink-0 flex-col content-stretch items-start justify-start"
          style={{ gap: "12px" }}>
          {credentials.map(renderCredential)}
        </div>
      ) : (
        <div className="py-8 text-center">
          <p
            className="text-lg"
            style={{
              fontFamily: theme?.fonts?.paragraph,
              color: theme?.colors?.textDark
            }}>
            No credentials available
          </p>
        </div>
      )}

      {/* Verified Badge */}
      {credentials.length > 0 && renderVerifiedBadge()}
    </div>
  )

  return (
    <section
      className={twMerge(
        "relative box-border flex size-full flex-col content-stretch items-center justify-start gap-8 px-0 py-16",
        isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
      )}
      style={{ backgroundColor: sectionBackgroundColor }}>
      <AnimatedSection className="flex w-full max-w-[720px] flex-col items-center justify-start gap-8">
        {renderContent()}
      </AnimatedSection>
    </section>
  )
}

export default CredentialsSection
